'use client';
import React from 'react';
import { useTranslation } from '@/hooks/useTranslation';
import Image from 'next/image';
import { BiCheck, BiSolidEnvelopeOpen, BiSolidGroup, BiSolidRocket, BiSolidWallet } from 'react-icons/bi';
import { RiSparkling2Line } from 'react-icons/ri';
import { HiOutlineArrowRight } from 'react-icons/hi2';
import HowItWorksStep from '@/components/blocks/HowItWorksStep';

type Props = { locale: string };

function AffiliationHowItWorks({ locale }: Props) {
  const { t } = useTranslation(locale);

  const steps = [
    {
      icon: BiSolidEnvelopeOpen,
      title: t('AffiliationPrograms/howItWorks/steps/applyToProgram/title'),
      description: t('AffiliationPrograms/howItWorks/steps/applyToProgram/description'),
    },
    {
      icon: BiSolidGroup,
      title: t('AffiliationPrograms/howItWorks/steps/shareWithAudience/title'),
      description: t('AffiliationPrograms/howItWorks/steps/shareWithAudience/description'),
    },
    {
      icon: BiSolidWallet,
      title: t('AffiliationPrograms/howItWorks/steps/startEarning/title'),
      description: t('AffiliationPrograms/howItWorks/steps/startEarning/description'),
    },
    {
      icon: BiSolidRocket,
      title: t('AffiliationPrograms/howItWorks/steps/trackGrowth/title'),
      description: t('AffiliationPrograms/howItWorks/steps/trackGrowth/description'),
    },
  ];

  const audienceTypes = [
    {
      title: t('AffiliationPrograms/section_who_is_it/youtubers'),
      image: 'https://cdn.1cdn.app/application/LOUISEBOT/24111610161415_youtuber.png',
      alt: 'YouTubers',
      features: [
        t('AffiliationPrograms/section_who_is_it/youtubers_details/video_reviews'),
        t('AffiliationPrograms/section_who_is_it/youtubers_details/unboxing_videos'),
        t('AffiliationPrograms/section_who_is_it/youtubers_details/step_by_step_tutorials'),
        t('AffiliationPrograms/section_who_is_it/youtubers_details/live_streaming'),
        t('AffiliationPrograms/section_who_is_it/youtubers_details/social_media_content'),
        t('AffiliationPrograms/section_who_is_it/youtubers_details/giveaways'),
      ],
    },
    {
      title: t('AffiliationPrograms/section_who_is_it/bloggers'),
      image: 'https://cdn.1cdn.app/application/LOUISEBOT/2411161016142_bloggers.png',
      alt: 'Bloggers',
      features: [
        t('AffiliationPrograms/section_who_is_it/bloggers_details/articles'),
        t('AffiliationPrograms/section_who_is_it/bloggers_details/tutorials'),
        t('AffiliationPrograms/section_who_is_it/bloggers_details/guides'),
        t('AffiliationPrograms/section_who_is_it/bloggers_details/reviews'),
        t('AffiliationPrograms/section_who_is_it/bloggers_details/case_studies'),
        t('AffiliationPrograms/section_who_is_it/bloggers_details/pop_ups'),
      ],
    },
    {
      title: t('AffiliationPrograms/section_who_is_it/connected_services'),
      image: 'https://cdn.1cdn.app/application/LOUISEBOT/24111610161474_connected_service.png',
      alt: 'Connected Services',
      features: [
        t('AffiliationPrograms/section_who_is_it/connected_services_details/landing_pages'),
        t('AffiliationPrograms/section_who_is_it/connected_services_details/promotional_campaigns'),
        t('AffiliationPrograms/section_who_is_it/connected_services_details/email_newsletters'),
        t('AffiliationPrograms/section_who_is_it/connected_services_details/social_media_content'),
        t('AffiliationPrograms/section_who_is_it/connected_services_details/articles_and_podcasts'),
      ],
    },
    {
      title: t('AffiliationPrograms/section_who_is_it/web_design_studies'),
      image: 'https://cdn.1cdn.app/application/LOUISEBOT/24111610161427_web_agencies.png',
      alt: 'Web Agencies',
      features: [
        t('AffiliationPrograms/section_who_is_it/web_design_studies_details/showcases'),
        t('AffiliationPrograms/section_who_is_it/web_design_studies_details/links_in_partner_blocks'),
        t('AffiliationPrograms/section_who_is_it/web_design_studies_details/themes_and_templates'),
        t('AffiliationPrograms/section_who_is_it/web_design_studies_details/website_examples'),
        t('AffiliationPrograms/section_who_is_it/web_design_studies_details/resources_page'),
      ],
    },
  ];

  return (
    <section className="tw-py-10 lg:tw-py-10 tw-bg-transparent tw-font-['Lexend']">
      <div className="tw-container tw-mx-auto tw-px-4 sm:tw-px-6 lg:tw-px-8 tw-max-w-7xl">
        {/* How It Works Section */}
        <div className="tw-mb-32">
          <div className="tw-text-center tw-mb-16">
            <div className="tw-inline-flex tw-items-center tw-gap-2 tw-bg-blue-100 tw-text-blue-700 tw-px-4 tw-py-2 tw-rounded-full tw-text-sm tw-font-medium tw-mb-6">
              <RiSparkling2Line className="tw-w-4 tw-h-4" />
              {t('AffiliationPrograms/howItWorks/badge')}
            </div>
            <h2 className="tw-text-3xl sm:tw-text-4xl lg:tw-text-5xl tw-font-bold tw-text-gray-900 tw-mb-6 tw-leading-tight">
              {t('AffiliationPrograms/howItWorks/title')}
            </h2>
            <p className="tw-text-lg tw-text-gray-600 tw-max-w-3xl tw-mx-auto tw-leading-relaxed">
              {t('AffiliationPrograms/howItWorks/description')}
            </p>
          </div>

          <div className="tw-space-y-0 lg:tw-space-y-16">
            {steps.map((step, index) => (
              <div key={index} className="tw-relative">
                <HowItWorksStep
                  step={index + 1}
                  icon={step.icon}
                  title={step.title}
                  description={step.description}
                  isReversed={index % 2 === 1}
                  isLast={index === steps.length - 1}
                />

                {/* Enhanced connection line for desktop */}
                {index < steps.length - 1 && (
                  <div className="tw-hidden lg:tw-block tw-absolute tw-top-full tw-left-1/2 tw-transform tw--translate-x-1/2 tw-z-10">
                    <div className="tw-flex tw-flex-col tw-items-center tw-space-y-2">
                      <div className="tw-w-0.5 tw-h-8 tw-bg-gradient-to-b tw-from-emerald-300 tw-to-cyan-300 tw-rounded-full"></div>
                      <div className="tw-w-3 tw-h-3 tw-bg-gradient-to-br tw-from-emerald-400 tw-to-cyan-500 tw-rounded-full tw-animate-pulse"></div>
                      <div className="tw-w-0.5 tw-h-8 tw-bg-gradient-to-b tw-from-emerald-300 tw-to-cyan-300 tw-rounded-full"></div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Who Is It For Section */}
        <div>
          <div className="tw-text-center tw-mb-16">
            <h2 className="tw-text-3xl sm:tw-text-4xl lg:tw-text-5xl tw-font-bold tw-text-gray-900 tw-mb-6 tw-leading-tight">
              {t('AffiliationPrograms/who_is_it_for')}
            </h2>
            <p className="tw-text-lg tw-text-gray-600 tw-max-w-3xl tw-mx-auto">
              {t('AffiliationPrograms/who-is-it-for-description')}
            </p>
          </div>

          <div className="tw-grid md:tw-grid-cols-2 tw-gap-8">
            {audienceTypes.map((type, index) => (
              <div
                key={index}
                className="tw-group tw-bg-gray-50 tw-rounded-3xl tw-overflow-hidden tw-transition-all tw-duration-300 hover:tw-shadow-2xl hover:tw-scale-[1.02]"
              >
                <div className="tw-grid md:tw-grid-cols-2 tw-h-full">
                  {/* Content */}
                  <div className="tw-p-8 tw-flex tw-flex-col tw-justify-between">
                    <div>
                      <h3 className="tw-text-xl tw-font-bold tw-text-gray-900 tw-mb-6">{type.title}</h3>

                      <ul className="tw-space-y-3">
                        {type.features.map((feature, featureIndex) => (
                          <li key={featureIndex} className="tw-flex tw-items-start tw-gap-3">
                            <BiCheck className="tw-w-5 tw-h-5 tw-text-green-500 tw-flex-shrink-0 tw-mt-0.5" />
                            <span className="tw-text-sm tw-text-gray-700 tw-leading-relaxed">{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>

                  {/* Image */}
                  <div className="tw-relative tw-overflow-hidden">
                    <Image
                      priority={index < 2}
                      quality={100}
                      src={type.image}
                      alt={type.alt}
                      width={300}
                      height={400}
                      className="tw-w-full tw-h-full tw-object-cover tw-transition-transform tw-duration-300 group-hover:tw-scale-110"
                    />
                    <div className="tw-absolute tw-inset-0 tw-bg-gradient-to-t tw-from-black/20 tw-to-transparent tw-opacity-0 group-hover:tw-opacity-100 tw-transition-opacity tw-duration-300"></div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

export default AffiliationHowItWorks;
